import { Fragment, type ReactNode } from 'react';

/**
 * Just enough markdown for the help articles, rendered straight to React
 * elements - no HTML string, so nothing to sanitise and no dependency.
 *
 * Blocks: # / ## / ### headings, paragraphs, - and 1. lists, > tips.
 * Inline: **bold**, *italic*, `code`, [text](href).
 *
 * Links: `help:<slug>` opens that article, `/path` navigates in the app,
 * anything else opens in a new tab.
 */
interface Props {
  source: string;
  onOpenArticle: (slug: string) => void;
  onNavigate: (path: string) => void;
}

const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

const Markdown = ({ source, onOpenArticle, onNavigate }: Props) => {
  const inline = (text: string): ReactNode[] =>
    text.split(INLINE).map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
        return <code key={i} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px] text-slate-900">{part.slice(1, -1)}</code>;
      }
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
      if (link) {
        const [, label, href] = link;
        const cls = 'font-semibold text-[#0047AB] underline-offset-2 hover:underline';
        if (href.startsWith('help:')) {
          return <button key={i} type="button" className={cls} onClick={() => onOpenArticle(href.slice(5))}>{label}</button>;
        }
        if (href.startsWith('/')) {
          return <button key={i} type="button" className={cls} onClick={() => onNavigate(href)}>{label}</button>;
        }
        return <a key={i} href={href} target="_blank" rel="noreferrer" className={cls}>{label}</a>;
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return <em key={i}>{part.slice(1, -1)}</em>;
      }
      return <Fragment key={i}>{part}</Fragment>;
    });

  const blocks: ReactNode[] = [];
  const lines = source.split(/\r?\n/);
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const cls = level === 1
        ? 'mt-6 mb-2 text-lg font-bold text-slate-900'
        : level === 2
          ? 'mt-6 mb-2 text-base font-bold text-slate-900'
          : 'mt-4 mb-1 text-sm font-bold text-slate-900';
      const Tag = level === 1 ? 'h3' : level === 2 ? 'h4' : 'h5';
      blocks.push(<Tag key={i} className={cls}>{inline(heading[2])}</Tag>);
      i++;
      continue;
    }

    if (/^\s*(-|\*|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*(-|\*|\d+\.)\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*(-|\*|\d+\.)\s+/, ''));
        i++;
      }
      const ListTag = ordered ? 'ol' : 'ul';
      blocks.push(
        <ListTag key={i} className={`my-2 space-y-1.5 pl-5 ${ordered ? 'list-decimal' : 'list-disc'} marker:text-slate-500`}>
          {items.map((item, j) => <li key={j}>{inline(item)}</li>)}
        </ListTag>,
      );
      continue;
    }

    if (line.startsWith('>')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) {
        quote.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      blocks.push(
        <div key={i} className="my-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
          {inline(quote.join(' '))}
        </div>,
      );
      continue;
    }

    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,3}\s|>|\s*(-|\*|\d+\.)\s+)/.test(lines[i])
    ) {
      para.push(lines[i].trim());
      i++;
    }
    blocks.push(<p key={i} className="my-2">{inline(para.join(' '))}</p>);
  }

  return <div className="text-sm leading-relaxed text-slate-600">{blocks}</div>;
};

export default Markdown;
